import React, { useEffect, useMemo, useState } from "react";
import { Filter, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { EMPTY_FILTER_STATE, countActiveFilters } from "@/lib/watchlistSortFilter";

const STATUS_OPTIONS = [
  { key: "undervalued",   t_key: "watchlist.filter.status_undervalued",   fallback: "Undervalued",   dot: "#3FAE89" },
  { key: "fairly_valued", t_key: "watchlist.filter.status_fair",          fallback: "Fairly valued", dot: "#E0B341" },
  { key: "overvalued",    t_key: "watchlist.filter.status_overvalued",    fallback: "Overvalued",    dot: "#E5484D" },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const upd = () => setIsMobile(mq.matches);
    upd();
    if (mq.addEventListener) { mq.addEventListener("change", upd); return () => mq.removeEventListener("change", upd); }
    mq.addListener(upd); return () => mq.removeListener(upd);
  }, []);
  return isMobile;
}

/**
 * WatchlistFiltersButton (Phase 8.WLNAV.v1)
 * ─────────────────────────────────────────
 * Single compact "Filters" control that:
 *   - opens a Popover on desktop
 *   - opens a Sheet (bottom-anchored) on mobile
 *
 * All filtering is client-side against the already-loaded items list.
 * No network calls, no /api/analyze triggers.
 *
 * Props:
 *   value      : filter state ({ statuses, labels, sectorEmojis })
 *   onChange   : (nextValue) => void
 *   availableLabels : array of label strings pulled from current items
 *   availableSectors: array of { emoji, code } pulled from current items + curated palette
 *   t          : translator
 */
export default function WatchlistFiltersButton({ value, onChange, availableLabels, availableSectors, t }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const tr = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };

  const count = countActiveFilters(value);
  const state = value || EMPTY_FILTER_STATE;

  const toggle = (bucket, key) => {
    const cur = new Set(state[bucket] || []);
    if (cur.has(key)) cur.delete(key); else cur.add(key);
    onChange({ ...state, [bucket]: Array.from(cur) });
  };
  const clearAll = () => onChange({ ...EMPTY_FILTER_STATE });

  const trigger = (
    <Button
      size="sm"
      variant="outline"
      data-testid="watchlist-filters-button"
      className="h-9 whitespace-nowrap relative"
      onClick={() => setOpen(true)}
    >
      <Filter className="w-3.5 h-3.5 mr-1.5" />
      <span className="text-xs">{tr("watchlist.filter.button", "Filters")}</span>
      {count > 0 ? (
        <span
          data-testid="watchlist-filters-button-count"
          className="ml-2 inline-flex items-center justify-center rounded-full text-[10px] font-medium min-w-[18px] h-[18px] px-1"
          style={{ background: "rgba(49,130,206,0.15)", color: "#3182CE", border: "1px solid rgba(49,130,206,0.35)" }}
        >
          {count}
        </span>
      ) : (
        <ChevronDown className="w-3 h-3 ml-1 opacity-60" />
      )}
    </Button>
  );

  const body = (
    <div className="space-y-5" data-testid="watchlist-filters-panel">
      {/* Valuation status */}
      <FilterSection title={tr("watchlist.filter.valuation_status", "Valuation status")}>
        <div className="flex flex-wrap gap-1.5" data-testid="watchlist-filter-statuses">
          {STATUS_OPTIONS.map((o) => {
            const active = (state.statuses || []).includes(o.key);
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => toggle("statuses", o.key)}
                data-testid={`watchlist-filter-status-${o.key}`}
                data-active={active ? "true" : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border transition-colors ${active ? "" : "hover:bg-secondary/40"}`}
                style={{
                  borderColor: active ? `${o.dot}88` : "var(--border)",
                  color: active ? o.dot : "inherit",
                  background: active ? `${o.dot}1A` : "transparent",
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: o.dot }} />
                {tr(o.t_key, o.fallback)}
              </button>
            );
          })}
        </div>
      </FilterSection>

      {/* Labels */}
      {(availableLabels || []).length > 0 ? (
        <FilterSection title={tr("watchlist.filter.labels", "Labels")}>
          <div className="flex flex-wrap gap-1.5" data-testid="watchlist-filter-labels">
            {availableLabels.map((l) => {
              const active = (state.labels || []).includes(l);
              const trLabel = t(`watchlist.suggested_labels.${l}`);
              const display = trLabel && trLabel !== `watchlist.suggested_labels.${l}` ? trLabel : l;
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggle("labels", l)}
                  data-testid={`watchlist-filter-label-${l}`}
                  data-active={active ? "true" : undefined}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium border transition-colors ${active ? "" : "hover:bg-secondary/40"}`}
                  style={{
                    borderColor: active ? "rgba(49,130,206,0.55)" : "var(--border)",
                    color: active ? "#3182CE" : "inherit",
                    background: active ? "rgba(49,130,206,0.10)" : "transparent",
                  }}
                >
                  {display}
                </button>
              );
            })}
          </div>
        </FilterSection>
      ) : null}

      {/* Sector & themes */}
      {(availableSectors || []).length > 0 ? (
        <FilterSection title={tr("watchlist.filter.sectors", "Sector & themes")}>
          <div className="flex flex-wrap gap-1.5" data-testid="watchlist-filter-sectors">
            {availableSectors.map((s) => {
              const active = (state.sectorEmojis || []).includes(s.emoji);
              const trSector = t(`watchlist.sectors.${s.code}`);
              const label = trSector && trSector !== `watchlist.sectors.${s.code}` ? trSector : s.code;
              return (
                <button
                  key={s.code + s.emoji}
                  type="button"
                  onClick={() => toggle("sectorEmojis", s.emoji)}
                  data-testid={`watchlist-filter-sector-${s.code}`}
                  data-active={active ? "true" : undefined}
                  className={`inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-0.5 text-[11px] font-medium border transition-colors ${active ? "" : "hover:bg-secondary/40"}`}
                  style={{
                    borderColor: active ? "rgba(49,130,206,0.55)" : "var(--border)",
                    color: active ? "#3182CE" : "inherit",
                    background: active ? "rgba(49,130,206,0.10)" : "transparent",
                  }}
                >
                  <span aria-hidden>{s.emoji}</span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </FilterSection>
      ) : null}

      {(!(availableLabels || []).length && !(availableSectors || []).length) ? (
        <div className="text-[11px] text-muted-foreground" data-testid="watchlist-filter-hint-empty-meta">
          {tr("watchlist.filter.hint_no_meta", "Add labels or sector tags in the row editor to unlock more filters.")}
        </div>
      ) : null}

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-border/60">
        <button
          type="button"
          onClick={clearAll}
          disabled={count === 0}
          data-testid="watchlist-filters-reset"
          className="text-[11px] font-medium inline-flex items-center gap-1 disabled:opacity-40 hover:text-foreground text-muted-foreground"
        >
          <X className="w-3 h-3" /> {tr("watchlist.filter.reset", "Reset filters")}
        </button>
        <Button size="sm" onClick={() => setOpen(false)} data-testid="watchlist-filters-done">
          {tr("common.done", "Done")}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto" data-testid="watchlist-filters-sheet">
            <SheetHeader className="mb-4">
              <SheetTitle>{tr("watchlist.filter.button", "Filters")}</SheetTitle>
              <SheetDescription>{tr("watchlist.filter.sheet_desc", "Filter your Watchlist by valuation, labels or sectors.")}</SheetDescription>
            </SheetHeader>
            {body}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] max-h-[75vh] overflow-y-auto" data-testid="watchlist-filters-popover">
        {body}
      </PopoverContent>
    </Popover>
  );
}

function FilterSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{title}</div>
      {children}
    </div>
  );
}

/**
 * ActiveFilterChips — renders only currently active filters plus the
 * total count and a Reset button. Nothing renders when no filters are
 * active.
 */
export function ActiveFilterChips({ value, onChange, t, sectorLabelMap, labelDisplayMap }) {
  const state = value || EMPTY_FILTER_STATE;
  const count = countActiveFilters(state);
  if (count === 0) return null;
  const tr = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };

  const remove = (bucket, key) => {
    const next = { ...state, [bucket]: (state[bucket] || []).filter((x) => x !== key) };
    onChange(next);
  };
  const clearAll = () => onChange({ ...EMPTY_FILTER_STATE });

  const statusLabel = (k) => {
    const map = { undervalued: ["watchlist.filter.status_undervalued", "Undervalued"], fairly_valued: ["watchlist.filter.status_fair", "Fairly valued"], overvalued: ["watchlist.filter.status_overvalued", "Overvalued"] };
    return tr(map[k]?.[0] || k, map[k]?.[1] || k);
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="watchlist-active-filter-chips">
      <span className="text-[11px] text-muted-foreground mr-1">
        {tr("watchlist.filter.active_count", "Active filters:")} <span className="text-foreground font-medium">{count}</span>
      </span>
      {(state.statuses || []).map((s) => (
        <FilterChip key={`s-${s}`} testId={`watchlist-active-chip-status-${s}`} onRemove={() => remove("statuses", s)}>
          {statusLabel(s)}
        </FilterChip>
      ))}
      {(state.labels || []).map((l) => (
        <FilterChip key={`l-${l}`} testId={`watchlist-active-chip-label-${l}`} onRemove={() => remove("labels", l)}>
          {(labelDisplayMap && labelDisplayMap[l]) || l}
        </FilterChip>
      ))}
      {(state.sectorEmojis || []).map((e) => (
        <FilterChip key={`e-${e}`} testId={`watchlist-active-chip-sector-${e}`} onRemove={() => remove("sectorEmojis", e)}>
          <span className="mr-1" aria-hidden>{e}</span>
          {sectorLabelMap && sectorLabelMap[e] ? sectorLabelMap[e] : ""}
        </FilterChip>
      ))}
      <button
        type="button"
        onClick={clearAll}
        data-testid="watchlist-active-chips-reset"
        className="text-[11px] font-medium inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-border/70 hover:bg-secondary/40"
      >
        <X className="w-3 h-3" /> {tr("watchlist.filter.reset", "Reset")}
      </button>
    </div>
  );
}

function FilterChip({ children, onRemove, testId }) {
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium border"
      style={{
        borderColor: "rgba(49,130,206,0.45)",
        color: "#3182CE",
        background: "rgba(49,130,206,0.10)",
      }}
    >
      {children}
      <button type="button" onClick={onRemove} aria-label="Remove filter" className="ml-1 rounded-full hover:bg-white/10">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
